import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type { TLanguage } from '@semios/app-platform-i18n-core'
import { createContext, useContext, useState } from 'react'
import { apiFetch } from 'utils/apiFetch'

export type AlertContactMethod = 'email' | 'sms'

export type ReportContactMethod = 'email' | null

export type GroupContact = {
  id: string
  name: string
  email: string | null
  sms: string | null
}

export type Contact = GroupContact & {
  groups?: {
    id: string
    name: string
  }[]
  language: TLanguage
  alertsContactMethods?: AlertContactMethod[] | null
  reportsContactMethods?: ReportContactMethod[] | null
}

export type Group = {
  id: number
  name: string
  contacts: GroupContact[]
  alertsContactMethods?: AlertContactMethod[] | null
  reportsContactMethods?: ReportContactMethod[] | null
}

type TGroupsAndContactsContext = {
  groups: Group[]
  contacts: Contact[]
  refreshContactsAndGroups: () => void
  deleteGroup: (groupId: Group['id']) => void
  deleteContact: (contactId: Contact['id']) => void
  loading: boolean
  initializeContactsAndGroups: () => void
}

const GroupsAndContactsContext = createContext<TGroupsAndContactsContext>({
  groups: [],
  contacts: [],
  refreshContactsAndGroups: () => null,
  deleteGroup: () => null,
  deleteContact: () => null,
  loading: true,
  initializeContactsAndGroups: () => null,
})

const requestBody = {
  contacts: {
    includeDeleted: false,
    attributes: {
      contactId: true,
      createdAt: true,
      email: true,
      language: true,
      name: true,
      sms: true,
      updatedAt: true,
      groupsForContact: { groupId: true },
    },
  },
  groups: {
    includeDeleted: false,
    attributes: {
      createdAt: true,
      groupId: true,
      name: true,
      updatedAt: true,
      contactsForGroup: { contactId: true },
    },
  },
} as const

const GroupsAndContactsProvider = ({ children }: { children: React.ReactNode }) => {
  const [groups, setGroups] = useState<Group[]>([])
  const [contacts, setContacts] = useState<Contact[]>([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const [loading, setLoading] = useState(false)

  const fetchData = async () => {
    setLoading(true)

    const resp = await apiFetch({
      url: '/user-profile',
      body: requestBody,
    })

    const response = resp as routes.UserProfile.Response<typeof requestBody>

    setGroups(() =>
      response.data.groups
        ? Object.values(response.data.groups).map((g) => ({
            contacts: Object.values(g.contactsForGroup).flatMap((c) => {
              const matchingContact = response.data.contacts?.[c.contactId]

              if (!matchingContact) return []

              return {
                id: matchingContact.contactId,
                name: matchingContact.name,
                email: matchingContact.email,
                sms: matchingContact.sms,
              }
            }),
            name: g.name,
            id: g.groupId,
          }))
        : [],
    )

    setContacts(() =>
      response.data.contacts
        ? Object.values(response.data.contacts).map((c) => ({
            email: c.email,
            groups: Object.values(c.groupsForContact).flatMap((g) => {
              const matchingGroup = response.data.groups?.[g.groupId]

              if (!matchingGroup) return []

              return {
                id: String(matchingGroup.groupId),
                name: matchingGroup.name,
              }
            }),
            id: c.contactId,
            language: c.language as TLanguage,
            name: c.name,
            sms: c.sms,
            alertsContactMethods: [
              ...(c.email ? ['email'] : []),
              ...(c.sms ? ['sms'] : []),
            ] as AlertContactMethod[],
            reportsContactMethods: c.email ? ['email'] : null,
          }))
        : [],
    )

    setLoading(false)

    setHasLoaded(true)
  }

  const initializeContactsAndGroups = () => {
    if (!hasLoaded && !loading) {
      fetchData()
    }
  }

  return (
    <GroupsAndContactsContext.Provider
      value={{
        groups,
        contacts,
        refreshContactsAndGroups: fetchData,
        loading,
        initializeContactsAndGroups,
        deleteGroup: (groupId: Group['id']) => {
          setContacts((prevContacts) =>
            prevContacts.map((contact) => ({
              ...contact,
              groups: contact.groups?.filter((group) => group.id !== String(groupId)),
            })),
          )

          setGroups((prevGroups) => prevGroups.filter((group) => group.id !== groupId))
        },
        deleteContact: (contactId: Contact['id']) => {
          setContacts((prevContacts) => prevContacts.filter((contact) => contact.id !== contactId))

          setGroups((prevGroups) =>
            prevGroups.map((group) => ({
              ...group,
              contacts: group.contacts.filter((contact) => contact.id !== contactId),
            })),
          )
        },
      }}
    >
      {children}
    </GroupsAndContactsContext.Provider>
  )
}

const useGroupsAndContacts = () => {
  return useContext(GroupsAndContactsContext)
}

export { GroupsAndContactsProvider, useGroupsAndContacts }
